import { ErrorDisplay, LoadingDisplay } from 'components/molecules';
import { AppGoBackButton, AppText, TopAppMenuProps } from 'components/atoms';
import { settlementTexts } from 'consts/text/index';
import { useDateFilter } from 'hooks/useDateFilter/useDateFilter';
import { DATE_FILTER_KEY } from 'consts/filters/common/filtersKeys';
import { parseStringIntoDayjsDate } from 'utils/parsers/dateTime/parseStringIntoDayjsDate';
import dayjs from 'dayjs';
import { Header } from 'components/templates/child/ChildDetails/components/Header/Header';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { useOutlet, useParams, useSearchParams } from 'react-router-dom';
import { Child, ChildId } from 'types';
import { useEffect } from 'react';
import { getFullName } from 'utils/getFullName';
import { appRoutes } from 'consts/paths/paths';
import { BalanceTag } from 'components/molecules/BalanceTag/BalanceTag';
import { Row } from 'antd';
import { getMonthFromDayjsMonth } from 'utils/getMonthFromDayjsMonth';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { ChildSettlementCostSplitList } from 'components/templates/settlement/ChildSettlementCostSplitList';
import { ChildTransactions } from 'components/templates/settlement/ChildTransactions/ChildTransactions';
import { StyledAppMenu, StyledRow } from './ChildSettlementDetails.styled';
import { ChildSettlementsSectionHeader } from './ChildSettlementsSectionHeader';
import { If } from '../../../atoms/If/If';
import {
    ChildSettlementDetailsMenuItems,
    ChildSettlementDetailsTabValue,
    ChildSettlementDetailsUrlSearchKey,
} from './ChildSettlementDetails.utils';
import { ChildSettlementMonthlyList } from './ChildSettlementMonthlyList/ChildSettlementMonthlyList';
import { AdditionalCostList } from './AdditionalCostList/AdditionalCostList';
import { ChildSettlementsCorrections } from './ChildSettlementsCorrections/ChildSettlementsCorrections';
import { ChildInvoices } from '../ChildInvoices/ChildInvoices';

export const ChildSettlementDetails = () => {
    const outlet = useOutlet();

    const pageRoute = appRoutes.app.settlement.children.pickedChild.base;
    const [searchParams, setSearchParams] = useSearchParams();
    const { childId } = useParams<{ childId: ChildId }>();

    const { dateFilterValue, handleChangeDateFilter, handleClickNextOrPrevDate } = useDateFilter({
        filterKey: DATE_FILTER_KEY,
    });

    const webParams = new URLSearchParams(window.location.search);
    const settlementType = webParams.get(
        ChildSettlementDetailsUrlSearchKey,
    ) as ChildSettlementDetailsTabValue;

    const selectedDate = dateFilterValue ? parseStringIntoDayjsDate(dateFilterValue) : dayjs();
    const selectedYear = selectedDate.year();
    const selectedMonth = getMonthFromDayjsMonth(selectedDate.month());
    const selectMenuKey: TopAppMenuProps['onClick'] = (menuInfo) => {
        const params = new URLSearchParams(window.location.search);
        params.set(ChildSettlementDetailsUrlSearchKey, menuInfo.key);
        setSearchParams(params, { replace: true });
    };
    const period = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`;

    const {
        data: childData,
        isFetching: isChildFetching,
        isError: isChildError,
    } = useAppQuery('CHILD', [childId], () =>
        StaffHTTPService.children.getChild(childId as ChildId),
    );

    const { data: childSettlement } = useAppQuery('CHILD_SETTLEMENT', [childId, period], () =>
        StaffHTTPService.settlements.getChildSettlement({
            childId: childId as ChildId,
            period,
        }),
    );

    const {
        data: childBalances,
        isFetching: isChildBalancesFetching,
        refetch: refetchBalance,
    } = useAppQuery('CHILD_SETTLEMENT_BALANCE', [childId, selectedMonth, selectedYear], () =>
        StaffHTTPService.settlements.getChildSettlementBalance({
            childId: childId as ChildId,
            yearMonth: period,
        }),
    );

    const handleRefetchBalance = async () => {
        await refetchBalance();
    };

    useEffect(() => {
        if (!childData) return;
        const params = new URLSearchParams(window.location.search);

        if (params.get(ChildSettlementDetailsUrlSearchKey)) return;
        if (params.get(pageRoute)) return;

        params.set(pageRoute, getFullName(childData.child.firstName, childData.child.lastName));
        params.set(ChildSettlementDetailsUrlSearchKey, 'MONTHLY_SETTLEMENT');

        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childData]);

    const isMonthlySettlement =
        searchParams.get(ChildSettlementDetailsUrlSearchKey) === 'MONTHLY_SETTLEMENT';

    if (isChildFetching || isChildBalancesFetching) {
        return <LoadingDisplay />;
    }

    if (!childData || !childId || isChildError) {
        return <ErrorDisplay />;
    }

    return (
        <>
            {outlet}
            <Row justify="space-between" align="middle">
                <AppGoBackButton />
            </Row>
            <Header
                {...(childData?.child as Child)}
                canManageChildAvatar={false}
                httpMethod={{
                    ...StaffHTTPService.files,
                    ...StaffHTTPService.children.profilePhoto,
                }}
                additionalElement={
                    <StyledRow>
                        <AppText marginLeft={0} textType="H5">
                            {settlementTexts.children.details.balances}
                        </AppText>
                        <AppText marginLeft={0}>
                            {settlementTexts.children.details.commonBalance}
                        </AppText>
                        <BalanceTag amount={childBalances?.generalBalance || 0} />
                        <AppText marginLeft={0}>
                            {settlementTexts.children.details.parentBalance}
                        </AppText>
                        <BalanceTag amount={childBalances?.parentBalance || 0} />
                        <AppText marginLeft={0}>
                            {settlementTexts.children.details.foundationBalance}
                        </AppText>
                        <BalanceTag amount={childBalances?.foundationBalance || 0} />
                        <AppText marginLeft={0}>
                            {settlementTexts.children.details.cateringBalance}
                        </AppText>
                        <BalanceTag amount={childBalances?.cateringBalance || 0} />
                    </StyledRow>
                }
            />
            <ChildTransactions refetchBalance={handleRefetchBalance} />
            <ChildSettlementsSectionHeader
                settlementType={settlementType}
                childId={childId}
                selectedDate={selectedDate}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                handleChangeDateFilter={handleChangeDateFilter}
                handleClickNextOrPrevDate={handleClickNextOrPrevDate}
            />
            <StyledAppMenu
                mode="horizontal"
                selectedKeys={[settlementType]}
                items={ChildSettlementDetailsMenuItems}
                onClick={selectMenuKey}
            />
            <If condition={isMonthlySettlement}>
                <ChildSettlementsCorrections
                    childId={childId as ChildId}
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                />
                <ChildSettlementMonthlyList childId={childId as ChildId} period={period} />
                <ChildInvoices childId={childId as ChildId} period={period} />
                <ChildSettlementCostSplitList
                    title={settlementTexts.children.costAllocation.title}
                    childId={childId as ChildId}
                    period={period}
                    isCostSplitModificationDisabled={
                        childSettlement?.monthlySettlementDetailsDto?.verified || false
                    }
                />
            </If>
            <If condition={!isMonthlySettlement}>
                <AdditionalCostList childId={childId!} period={period} />
            </If>
        </>
    );
};
